import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@shadcn-in-nx/ui/table";
import { useBluelagoon } from "@bluelagoon";
import { useEffect, useState, Fragment } from "react";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import { DialogNewSecret } from "./DialogNewSecret";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@shadcn-in-nx/ui/alert-dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    Trash2,
    Key,
    Search,
    ChevronDown,
    ChevronRight,
    Workflow,
    AppWindowMac,
    Edit,
    Copy,
    Check,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Input } from "@shadcn-in-nx/ui/input";
import { Badge } from "@shadcn-in-nx/ui/badge";
import MainLayout from "../Layouts/main";
import { Alert, AlertDescription, AlertTitle } from "@shadcn-in-nx/ui/alert";
import { Crown } from "lucide-react";

const Secrets = () => {
    const { t } = useTranslation();
    const { isFeatureEnabled } = useAuth();
    const hasSharedSecretsAccess = isFeatureEnabled("sharedSecrets");
    // Ensure user is authenticated
    const [deletingSecretId, setDeletingSecretId] = useState(null);
    const [expandedSecrets, setExpandedSecrets] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [editingSecret, setEditingSecret] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const {
        call: getSecrets,
        response: secretsResponse,
        isLoading,
        error,
    } = useBluelagoon({
        method: "getSecrets",
    });

    const { call: deleteSecret } = useBluelagoon({
        method: "deleteSecret",
    });

    useEffect(() => {
        fetchSecrets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSecrets = () => {
        getSecrets({}).catch((err) => {
            console.error("Failed to fetch secrets:", err);
        });
    };

    const handleSecretCreated = () => {
        fetchSecrets();
        setEditingSecret(null);
    };

    const handleDeleteSecret = async (secretId, secretName) => {
        setDeletingSecretId(secretId);
        try {
            await deleteSecret({ secretId });
            toast.success(t("secrets.secretDeleted", { name: secretName }));
            fetchSecrets();
        } catch (err) {
            console.error("Failed to delete secret:", err);
            toast.error(err?.message || t("secrets.failedToDeleteSecret"));
        } finally {
            setDeletingSecretId(null);
        }
    };

    const toggleSecretExpansion = (secretId) => {
        setExpandedSecrets((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(secretId)) {
                newSet.delete(secretId);
            } else {
                newSet.add(secretId);
            }
            return newSet;
        });
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleCopyName = async (name) => {
        try {
            await navigator.clipboard.writeText(name);
            setCopiedId(name);
            setTimeout(() => setCopiedId(null), 2000);
            toast.success(t("secrets.nameCopied"));
        } catch (err) {
            toast.error(t("secrets.failedToCopy"));
        }
    };

    const filteredSecrets =
        secretsResponse?.data?.secrets?.filter(
            (secret) =>
                secret.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                secret.description
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase())
        ) || [];

    if (error) {
        return (
            <MainLayout>
                <div className="p-4 text-red-500">
                    {t("secrets.errorLoadingSecrets", {
                        message: error.message,
                    })}
                </div>
            </MainLayout>
        );
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <MainLayout>
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="font-semibold text-xl">
                                {t("secrets.title")}
                            </h2>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                            {t("secrets.description")}
                        </p>
                    </div>
                    <DialogNewSecret
                        onSuccess={handleSecretCreated}
                        editingSecret={editingSecret}
                        onClose={() => setEditingSecret(null)}
                        disabled={!hasSharedSecretsAccess}
                    />
                </div>

                {/* Plan Restriction Alert */}
                {!hasSharedSecretsAccess && (
                    <Alert className="bg-purple-50 border-purple-200">
                        <Crown className="h-4 w-4 text-purple-600" />
                        <AlertTitle className="text-purple-800">
                            {t("appPreview.upgradeRequired") ||
                                "Upgrade Required"}
                        </AlertTitle>
                        <AlertDescription className="text-purple-700">
                            {t("secrets.sharedSecretsUpgradeMessage") ||
                                "Shared secrets are available on Innovator plan and above. Upgrade your plan to create and manage shared secrets across your workflows."}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Search Bar */}
                <div className="flex items-center gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                            type="text"
                            placeholder={t("secrets.searchPlaceholder")}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="pl-10"
                        />
                    </div>
                    {secretsResponse?.data?.secrets && (
                        <div className="text-sm text-gray-600">
                            {filteredSecrets.length} {t("secrets.secretsFound")}
                        </div>
                    )}
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[50px]"></TableHead>
                            <TableHead>{t("secrets.name")}</TableHead>
                            <TableHead>
                                {t("secrets.descriptionColumn")}
                            </TableHead>
                            <TableHead>{t("secrets.workflows")}</TableHead>
                            <TableHead>{t("secrets.lastUpdated")}</TableHead>
                            <TableHead className="w-[120px]">
                                {t("secrets.actions")}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            // Loading skeleton
                            [...Array(3)].map((_, index) => (
                                <TableRow key={`loading-${index}`}>
                                    <TableCell>
                                        <Skeleton className="h-4 w-4" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-[150px]" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-[200px]" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-[80px]" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-[100px]" />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton className="h-4 w-[80px]" />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : filteredSecrets.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="text-center py-8 text-muted-foreground"
                                >
                                    <Key className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>{t("secrets.noSecrets")}</p>
                                    <p className="text-sm">
                                        {t("secrets.noSecretsHint")}
                                    </p>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredSecrets.map((secret) => (
                                <Fragment key={secret.id}>
                                    <TableRow>
                                        <TableCell>
                                            {secret.workflows &&
                                                secret.workflows.length > 0 && (
                                                    <button
                                                        onClick={() =>
                                                            toggleSecretExpansion(
                                                                secret.id
                                                            )
                                                        }
                                                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                        title={
                                                            expandedSecrets.has(
                                                                secret.id
                                                            )
                                                                ? t(
                                                                      "secrets.collapseWorkflows"
                                                                  )
                                                                : t(
                                                                      "secrets.expandWorkflows"
                                                                  )
                                                        }
                                                    >
                                                        {expandedSecrets.has(
                                                            secret.id
                                                        ) ? (
                                                            <ChevronDown className="w-4 h-4 text-gray-500" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4 text-gray-500" />
                                                        )}
                                                    </button>
                                                )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Key className="w-4 h-4 text-amber-600" />
                                                <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                                                    {secret.name}
                                                </code>
                                                <button
                                                    onClick={() =>
                                                        handleCopyName(
                                                            secret.name
                                                        )
                                                    }
                                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                    title={t(
                                                        "secrets.copyName"
                                                    )}
                                                >
                                                    {copiedId ===
                                                    secret.name ? (
                                                        <Check className="w-3 h-3 text-green-500" />
                                                    ) : (
                                                        <Copy className="w-3 h-3 text-gray-400" />
                                                    )}
                                                </button>
                                            </div>
                                        </TableCell>
                                        <TableCell className="max-w-[250px] truncate">
                                            {secret.description || (
                                                <span className="text-muted-foreground italic">
                                                    {t("secrets.noDescription")}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {secret.workflows &&
                                            secret.workflows.length > 0 ? (
                                                <Badge
                                                    variant="secondary"
                                                    className="gap-1"
                                                >
                                                    {secret.workflows.some(
                                                        (ind) =>
                                                            ind.workflowType ===
                                                            "APP_V2"
                                                    ) ? (
                                                        <AppWindowMac className="w-3 h-3" />
                                                    ) : (
                                                        <Workflow className="w-3 h-3" />
                                                    )}

                                                    {secret.workflows.length}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground text-sm">
                                                    {t("secrets.notUsed")}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {formatDate(secret.updatedAt)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() =>
                                                        setEditingSecret(secret)
                                                    }
                                                    disabled={
                                                        !hasSharedSecretsAccess
                                                    }
                                                    title={t(
                                                        "secrets.editSecret"
                                                    )}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            disabled={
                                                                deletingSecretId ===
                                                                    secret.id ||
                                                                !hasSharedSecretsAccess
                                                            }
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>
                                                                {t(
                                                                    "secrets.deleteSecret"
                                                                )}
                                                            </AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                {t(
                                                                    "secrets.deleteSecretDescription",
                                                                    {
                                                                        name: secret.name,
                                                                    }
                                                                )}
                                                                {secret.workflows &&
                                                                    secret
                                                                        .workflows
                                                                        .length >
                                                                        0 && (
                                                                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800">
                                                                            {t(
                                                                                "secrets.deleteWarning",
                                                                                {
                                                                                    count: secret
                                                                                        .workflows
                                                                                        .length,
                                                                                }
                                                                            )}
                                                                        </div>
                                                                    )}
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>
                                                                {t(
                                                                    "secrets.cancel"
                                                                )}
                                                            </AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() =>
                                                                    handleDeleteSecret(
                                                                        secret.id,
                                                                        secret.name
                                                                    )
                                                                }
                                                                className="bg-red-600 hover:bg-red-700"
                                                            >
                                                                {t(
                                                                    "secrets.deleteSecret"
                                                                )}
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    {secret.workflows &&
                                        secret.workflows.length > 0 &&
                                        expandedSecrets.has(secret.id) && (
                                            <TableRow
                                                key={`${secret.id}-workflows`}
                                                className="bg-gray-50/50"
                                            >
                                                <TableCell
                                                    colSpan={6}
                                                    className="py-2"
                                                >
                                                    <div className="flex items-start gap-2 pl-6">
                                                        <div className="flex-1">
                                                            <div className="text-xs font-medium text-gray-600 mb-1">
                                                                {t(
                                                                    "secrets.usedInWorkflows"
                                                                )}
                                                                :
                                                            </div>
                                                            <div className="flex flex-wrap gap-2">
                                                                {secret.workflows.map(
                                                                    (
                                                                        workflow
                                                                    ) => (
                                                                        <span
                                                                            key={
                                                                                workflow.id
                                                                            }
                                                                            className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800"
                                                                        >
                                                                            {
                                                                                workflow.name
                                                                            }
                                                                        </span>
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                </Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </MainLayout>
    );
};

export default Secrets;
